import { useEffect, useRef, useCallback, useState } from 'preact/hooks'
import { useTranslation } from 'react-i18next'
import '@enigma/fe-signature'
import type { RectangleCoordinates } from '@enigma/fe-signature/models/sign-file.model'
import { Dialog } from './Dialog'
import { Button } from './Button'
import { Alert } from './Alert'
import { useDeviceDetection } from '@/hooks/useDeviceDetection'

interface AddDocumentDialogStep2Props {
  isOpen: boolean
  onClose: () => void
  selectedFile: File
  onCoordinatesChange: (coords: RectangleCoordinates | null) => void
  onBack: () => void
  onAdd: () => void
  isLoading?: boolean
  error?: string | null
  onErrorDismiss?: () => void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const GraphicSignatureComponent = 'graphic-signature-component' as any

interface GraphicSignatureElement extends HTMLElement {
  pdfUrl: string
  isFilePickerVisible: boolean
  isValidationTextVisible: boolean
  onSucces: (coords: RectangleCoordinates) => void
}

function GraphicSignatureSection({
  selectedFile,
  isError,
  isAlertVisible,
  onCoordinatesChange,
}: {
  selectedFile: File
  isError?: boolean
  isAlertVisible?: boolean
  onCoordinatesChange: (coords: RectangleCoordinates | null) => void
}) {
  const pdfUrl = URL.createObjectURL(selectedFile)
  const ref = useRef<GraphicSignatureElement | null>(null)

  useEffect(() => {
    return () => URL.revokeObjectURL(pdfUrl)
  }, [pdfUrl])

  const onSucces = useCallback(
    (coords: RectangleCoordinates) => {
      onCoordinatesChange(coords)
    },
    [onCoordinatesChange]
  )

  useEffect(() => {
    if (ref.current && pdfUrl) {
      ref.current.pdfUrl = pdfUrl
      ref.current.onSucces = onSucces
    }
  }, [pdfUrl, onSucces])

  const alertTookHeight = isAlertVisible ? 50 : 0
  const errorTookHeight = isError ? 70 : 0
  const totalTookHeight = 190 + alertTookHeight + errorTookHeight

  return (
    <div
      className='mx-5 flex-1 sm:flex-none'
      style={{ height: `calc(90dvh - ${totalTookHeight}px)` }}
    >
      {pdfUrl && (
        <GraphicSignatureComponent
          ref={ref}
          pdfUrl={pdfUrl}
          onSucces={onSucces}
        />
      )}
    </div>
  )
}

export function AddDocumentDialogStep2({
  isOpen,
  onClose,
  selectedFile,
  onCoordinatesChange,
  onBack,
  onAdd,
  isLoading = false,
  error = null,
  onErrorDismiss,
}: AddDocumentDialogStep2Props) {
  const { t } = useTranslation()
  const { isMobile } = useDeviceDetection()
  const [showInstructions, setShowInstructions] = useState(true)

  const footer = (
    <div className='flex flex-col xs:flex-row gap-8 xs:gap-8'>
      <Button
        type='secondary'
        color='primary'
        size='md'
        onClick={onBack}
        disabled={isLoading}
        className='w-full xs:flex-1 xs:min-w-[140px]'
      >
        {t('documentAdd.dialog.back')}
      </Button>
      <Button
        onClick={onAdd}
        disabled={isLoading}
        className='w-full xs:flex-1 xs:min-w-[140px]'
      >
        {t('documentAdd.dialog.add')}
      </Button>
    </div>
  )

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('documentAdd.dialog.step2Title')}
      size='xl'
      footer={footer}
    >
      {isLoading && (
        <div className='absolute inset-0 z-50 bg-white/80 flex items-center justify-center'>
          <div className='w-16 h-16 border-4 border-cencert-border-gray border-t-cencert-blue rounded-full animate-spin'></div>
        </div>
      )}
      <div className='pt-5 flex flex-col h-full'>
        {/* Instructional alert for graphic signature positioning */}
        {showInstructions && (
          <Alert
            variant='info'
            translationKey={
              isMobile
                ? 'documentAdd.graphicSignature.instructionAlertMobile'
                : 'documentAdd.graphicSignature.instructionAlertDesktop'
            }
            onDismiss={() => setShowInstructions(false)}
            className='mx-10 mb-2'
          />
        )}

        {/* Error message display */}
        {error && (
          <Alert
            variant='error'
            message={error}
            className='mx-10 mb-8'
            {...(onErrorDismiss && { onDismiss: onErrorDismiss })}
          />
        )}

        {/* STEP 2: Graphic Signature Positioning */}
        {selectedFile && selectedFile.type === 'application/pdf' && (
          <GraphicSignatureSection
            selectedFile={selectedFile}
            onCoordinatesChange={onCoordinatesChange}
            isError={Boolean(error)}
            isAlertVisible={showInstructions}
          />
        )}
      </div>
    </Dialog>
  )
}
